import { useState, useRef, useCallback } from 'react';
import { motion, useMotionValue, useTransform, useReducedMotion, AnimatePresence } from 'framer-motion';
import {
  X,
  CheckCircle2,
  Trophy,
  Target,
  ChevronRight,
  Volume2,
} from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useSoundEffects } from '../../../hooks/cabin/useSoundEffects';
import { useConfetti } from '../../../hooks/cabin/useConfetti';

/**
 * SwipeVocabularyCard — Tinder-style vocab card stack
 *
 * phase.content schema:
 * { cards: [{ word, definition_tr, definition_en, example_tr, example_en,
 *              phonetic, image_url, swipe_direction_correct }] }
 */
export default function SwipeVocabularyCard({
  phase,
  onPhaseComplete,
  checkAnswer,
  'data-testid': testId = 'swipe-vocab',
}) {
  const { t, lang } = useLanguage();
  const { play } = useSoundEffects();
  const { fire, fireSmall } = useConfetti();
  const shouldReduce = useReducedMotion();

  const cards = phase?.content?.cards || [];
  const maxXP = phase?.xp_in_phase || 0;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [knownCount, setKnownCount] = useState(0);
  const [unknownCount, setUnknownCount] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [swipingOut, setSwipingOut] = useState(null); // 'left' | 'right' | null
  // Server-side grading (Bug 3): her kart icin secilen yon toplanir
  const collectedAnswers = useRef([]);

  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-25, 25]);
  const leftOpacity = useTransform(x, [-100, 0], [1, 0]);
  const rightOpacity = useTransform(x, [0, 100], [0, 1]);

  const dragConstraints = { left: -300, right: 300, top: 0, bottom: 0 };
  const SWIPE_THRESHOLD = 100;

  const currentCard = cards[currentIndex];
  const definition =
    lang === 'tr'
      ? currentCard?.definition_tr
      : currentCard?.definition_en || currentCard?.definition_tr;
  const example =
    lang === 'tr'
      ? currentCard?.example_tr
      : currentCard?.example_en || currentCard?.example_tr;

  const advanceCard = useCallback(
    (direction) => {
      const idx = currentIndex;
      collectedAnswers.current = [
        ...collectedAnswers.current,
        { question_index: idx, direction },
      ];

      // Local fallback dogruluk (strip nedeniyle default 'right' olabilir)
      const localCorrect =
        (currentCard?.swipe_direction_correct || 'right') === direction;

      if (typeof checkAnswer === 'function') {
        // Server-truth: sayaclari sonradan duzeltir (UI'yi bloklamaz)
        checkAnswer({ phaseId: phase?.phase_id, questionIndex: idx, answer: { direction } })
          .then((res) => {
            const ok = res ? !!res.is_correct : localCorrect;
            if (ok) setKnownCount((k) => k + 1);
            else setUnknownCount((u) => u + 1);
          })
          .catch(() => {
            // Network/server error: fall back to local correctness so the card
            // still advances gracefully without an unhandled rejection.
            if (localCorrect) setKnownCount((k) => k + 1);
            else setUnknownCount((u) => u + 1);
          });
        play(localCorrect ? 'correct' : 'wrong');
        if (localCorrect && !shouldReduce) fireSmall({ y: 0.5 });
      } else {
        if (localCorrect) {
          setKnownCount((k) => k + 1);
          play('correct');
          if (!shouldReduce) fireSmall({ y: 0.5 });
        } else {
          setUnknownCount((u) => u + 1);
          play('wrong');
        }
      }

      setSwipingOut(direction);
      x.set(0);

      setTimeout(
        () => {
          setSwipingOut(null);
          setIsFlipped(false);
          const next = currentIndex + 1;
          if (next >= cards.length) {
            setIsDone(true);
            play('success');
            if (!shouldReduce) fire({ particleCount: 80 });
          } else {
            setCurrentIndex(next);
          }
        },
        shouldReduce ? 0 : 350
      );
    },
    [currentCard, currentIndex, cards.length, checkAnswer, phase, play, shouldReduce, fireSmall, fire, x]
  );

  const handleDragEnd = useCallback(
    (_, info) => {
      if (!isFlipped) return;
      const offset = info.offset.x;
      if (offset > SWIPE_THRESHOLD) {
        advanceCard('right');
      } else if (offset < -SWIPE_THRESHOLD) {
        advanceCard('left');
      } else {
        x.set(0);
      }
    },
    [isFlipped, advanceCard, x]
  );

  const handlePlayAudio = useCallback(() => {
    if ('speechSynthesis' in window) {
      const utt = new window.SpeechSynthesisUtterance(currentCard?.word || '');
      utt.lang = 'en-US';
      utt.rate = 0.9;
      window.speechSynthesis.speak(utt);
    }
  }, [currentCard]);

  // Completion screen
  if (isDone) {
    const accuracy = cards.length > 0 ? Math.round((knownCount / cards.length) * 100) : 0;
    const isPerfect = accuracy === 100;
    const isGood = accuracy >= 70;
    const score = maxXP > 0 ? Math.round((accuracy / 100) * maxXP) : knownCount;

    return (
      <motion.div
        initial={shouldReduce ? {} : { opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col items-center gap-6 w-full max-w-lg mx-auto text-center py-6"
        data-testid={`${testId}-complete`}
      >
        <div
          className={`w-20 h-20 rounded-2xl flex items-center justify-center shadow-[0_4px_0_0_rgba(0,0,0,0.15)] ${
            isPerfect
              ? 'bg-gradient-to-br from-amber-400 to-amber-500'
              : isGood
              ? 'bg-gradient-to-br from-emerald-400 to-emerald-500'
              : 'bg-gradient-to-br from-orange-400 to-orange-500'
          }`}
        >
          {isPerfect ? (
            <Trophy size={36} className="text-white" aria-hidden="true" />
          ) : (
            <Target size={36} className="text-white" aria-hidden="true" />
          )}
        </div>

        <div>
          <h2 className="text-2xl font-black text-[var(--text-main)] mb-1">
            {isPerfect
              ? t('cabin.exercise.swipeCard.perfect')
              : isGood
              ? t('cabin.exercise.swipeCard.great')
              : t('cabin.exercise.swipeCard.keepGoing')}
          </h2>
          <p className="text-sm text-[var(--text-muted)]">
            {t('cabin.exercise.swipeCard.resultDesc')
              .replace('{known}', knownCount)
              .replace('{total}', cards.length)}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 w-full">
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-4 flex flex-col items-center gap-1">
            <CheckCircle2 size={20} className="text-emerald-500" aria-hidden="true" />
            <span className="text-2xl font-black text-emerald-600">{knownCount}</span>
            <span className="text-xs text-emerald-700 font-semibold">
              {t('cabin.exercise.swipeCard.knownIt')}
            </span>
          </div>
          <div className="bg-rose-50 border-2 border-rose-200 rounded-2xl p-4 flex flex-col items-center gap-1">
            <X size={20} className="text-rose-500" aria-hidden="true" />
            <span className="text-2xl font-black text-rose-600">{unknownCount}</span>
            <span className="text-xs text-rose-700 font-semibold">
              {t('cabin.exercise.swipeCard.dontKnow')}
            </span>
          </div>
        </div>

        {/* Accuracy bar */}
        <div className="w-full">
          <div className="flex justify-between text-xs font-semibold text-[var(--text-muted)] mb-1.5">
            <span>{t('cabin.exercise.swipeCard.accuracy')}</span>
            <span
              className={
                isPerfect
                  ? 'text-amber-600'
                  : isGood
                  ? 'text-emerald-600'
                  : 'text-orange-600'
              }
            >
              {accuracy}%
            </span>
          </div>
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden border border-gray-300">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${accuracy}%` }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className={`h-full rounded-full ${
                isPerfect
                  ? 'bg-gradient-to-r from-amber-400 to-amber-500'
                  : isGood
                  ? 'bg-gradient-to-r from-emerald-400 to-emerald-500'
                  : 'bg-gradient-to-r from-orange-400 to-orange-500'
              }`}
            />
          </div>
        </div>

        <motion.button
          whileHover={shouldReduce ? {} : { scale: 1.02 }}
          whileTap={shouldReduce ? {} : { scale: 0.98 }}
          onClick={() =>
            onPhaseComplete({
              score,
              maxScore: maxXP,
              answers: collectedAnswers.current, // [{question_index, direction}]
              timeSpent: 0,
            })
          }
          className="flex items-center gap-2 px-8 py-3 bg-[var(--brand-primary)] text-white font-bold rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
          data-testid={`${testId}-finish`}
        >
          {t('exercise.finish')}
          <ChevronRight size={18} aria-hidden="true" />
        </motion.button>
      </motion.div>
    );
  }

  if (!currentCard) return null;

  const progress = (currentIndex / cards.length) * 100;

  return (
    <div
      className="flex flex-col gap-5 w-full max-w-lg mx-auto select-none"
      data-testid={testId}
    >
      {/* Progress & counter */}
      <div className="flex items-center justify-between text-sm text-[var(--text-muted)]">
        <span>
          {currentIndex + 1} / {cards.length}
        </span>
        <div className="flex-1 mx-4 h-2 bg-gray-200 rounded-full border border-gray-300 overflow-hidden">
          <motion.div
            className="h-full bg-[var(--brand-primary)] rounded-full"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
        <span className="text-xs text-emerald-600 font-bold">{knownCount} ok</span>
      </div>

      {/* Swipe direction hints */}
      <div className="flex justify-between px-2">
        <motion.div
          style={{ opacity: leftOpacity }}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-100 border border-rose-200 rounded-full"
        >
          <X size={14} className="text-rose-500" aria-hidden="true" />
          <span className="text-xs font-bold text-rose-600">
            {t('cabin.exercise.swipeCard.dontKnow')}
          </span>
        </motion.div>
        <motion.div
          style={{ opacity: rightOpacity }}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 border border-emerald-200 rounded-full"
        >
          <span className="text-xs font-bold text-emerald-600">
            {t('cabin.exercise.swipeCard.knownIt')}
          </span>
          <CheckCircle2 size={14} className="text-emerald-500" aria-hidden="true" />
        </motion.div>
      </div>

      {/* Card stack */}
      <div className="relative h-72 w-full">
        {/* Background stack cards */}
        {currentIndex < cards.length - 1 && (
          <>
            <div className="absolute inset-0 bg-white border-2 border-gray-200 rounded-2xl transform scale-[0.92] translate-y-4 opacity-50" />
            <div className="absolute inset-0 bg-white border-2 border-gray-200 rounded-2xl transform scale-[0.96] translate-y-2 opacity-75" />
          </>
        )}

        {/* Main draggable card */}
        <AnimatePresence mode="wait">
          {swipingOut === null && (
            <motion.div
              key={currentIndex}
              style={{ x, rotate }}
              drag={isFlipped ? 'x' : false}
              dragConstraints={dragConstraints}
              dragElastic={0.3}
              onDragEnd={handleDragEnd}
              initial={shouldReduce ? {} : { opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={
                shouldReduce
                  ? {}
                  : {
                      x: swipingOut === 'right' ? 400 : -400,
                      opacity: 0,
                      transition: { duration: 0.3 },
                    }
              }
              className="absolute inset-0 bg-white border-2 border-[var(--border-light)] rounded-2xl shadow-[0_4px_0_0_rgba(0,0,0,0.08)] cursor-pointer overflow-hidden"
              onClick={() => {
                if (!isFlipped) {
                  play('click');
                  setIsFlipped(true);
                }
              }}
              data-testid={`${testId}-card`}
              aria-label={
                isFlipped
                  ? t('exercise.card_back')
                  : t('exercise.card_front')
              }
            >
              {/* Left overlay */}
              <motion.div
                style={{ opacity: leftOpacity }}
                className="absolute inset-0 bg-gradient-to-r from-rose-400/20 to-transparent z-10 pointer-events-none flex items-center justify-start pl-6 rounded-2xl"
              >
                <div className="w-12 h-12 rounded-full bg-rose-500 flex items-center justify-center">
                  <X size={24} className="text-white" aria-hidden="true" />
                </div>
              </motion.div>

              {/* Right overlay */}
              <motion.div
                style={{ opacity: rightOpacity }}
                className="absolute inset-0 bg-gradient-to-l from-emerald-400/20 to-transparent z-10 pointer-events-none flex items-center justify-end pr-6 rounded-2xl"
              >
                <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center">
                  <CheckCircle2 size={24} className="text-white" aria-hidden="true" />
                </div>
              </motion.div>

              {/* Card face — front / back */}
              <div className="h-full flex flex-col items-center justify-center p-6 gap-3">
                {!isFlipped ? (
                  // Front
                  <>
                    {currentCard.image_url && (
                      <img
                        src={currentCard.image_url}
                        alt=""
                        aria-hidden="true"
                        className="w-16 h-16 object-contain rounded-xl"
                      />
                    )}
                    <p className="text-3xl font-black text-[var(--text-main)] text-center">
                      {currentCard.word}
                    </p>
                    {currentCard.phonetic && (
                      <p className="text-sm text-[var(--text-muted)]">{currentCard.phonetic}</p>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlayAudio();
                      }}
                      aria-label={t('exercise.play_audio')}
                      className="mt-1 p-2 rounded-full bg-sky-50 hover:bg-sky-100 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
                    >
                      <Volume2 size={18} className="text-sky-500" aria-hidden="true" />
                    </button>
                    <p className="text-xs text-[var(--text-muted)] mt-2">
                      {t('exercise.tap_to_flip')}
                    </p>
                  </>
                ) : (
                  // Back
                  <>
                    <div className="w-full bg-gradient-to-br from-sky-500 to-indigo-600 rounded-xl p-4 text-white text-center">
                      <p className="text-xl font-bold mb-1">{definition}</p>
                      {example && (
                        <p className="text-xs text-white/80 italic mt-2">{example}</p>
                      )}
                    </div>
                    <p className="text-xs text-[var(--text-muted)]">
                      {t('cabin.exercise.swipeCard.swipeHint')}
                    </p>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Buttons for non-touch */}
      {isFlipped ? (
        <div className="grid grid-cols-2 gap-3">
          <motion.button
            whileHover={shouldReduce ? {} : { scale: 1.02 }}
            whileTap={shouldReduce ? {} : { scale: 0.98 }}
            onClick={() => advanceCard('left')}
            className="flex items-center justify-center gap-2 py-3 border-2 border-rose-300 text-rose-600 hover:bg-rose-50 rounded-xl font-bold text-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-400"
            data-testid={`${testId}-dont-know`}
          >
            <X size={16} aria-hidden="true" />
            {t('cabin.exercise.swipeCard.dontKnow')}
          </motion.button>
          <motion.button
            whileHover={shouldReduce ? {} : { scale: 1.02 }}
            whileTap={shouldReduce ? {} : { scale: 0.98 }}
            onClick={() => advanceCard('right')}
            className="flex items-center justify-center gap-2 py-3 bg-emerald-500 text-white hover:bg-emerald-600 rounded-xl font-bold text-sm shadow-[0_3px_0_0_#059669] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-500"
            data-testid={`${testId}-know`}
          >
            <CheckCircle2 size={16} aria-hidden="true" />
            {t('cabin.exercise.swipeCard.knownIt')}
          </motion.button>
        </div>
      ) : (
        <motion.button
          whileHover={shouldReduce ? {} : { scale: 1.02 }}
          whileTap={shouldReduce ? {} : { scale: 0.98 }}
          onClick={() => {
            play('click');
            setIsFlipped(true);
          }}
          className="w-full py-3 bg-[var(--brand-primary)] text-white font-bold rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
          data-testid={`${testId}-flip`}
        >
          {t('exercise.flip')}
        </motion.button>
      )}

      {/* Bottom score row */}
      <div className="flex justify-center gap-8 text-sm">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-emerald-500" />
          <span className="text-[var(--text-muted)]">
            {knownCount} {t('cabin.exercise.swipeCard.knownIt')}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-rose-500" />
          <span className="text-[var(--text-muted)]">
            {unknownCount} {t('cabin.exercise.swipeCard.dontKnow')}
          </span>
        </div>
      </div>
    </div>
  );
}
